import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! 👋 Welcome to Acuit CRM. How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [pricingData, setPricingData] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Fetch pricing data when component mounts
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const response = await api.get('/pricing');
        if (response.data.success && Array.isArray(response.data.data)) {
          setPricingData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching pricing data for chatbot:', error);
      }
    };
    fetchPricingData();
  }, []);

  const quickReplies = [
    "What is Acuit CRM?",
    "Pricing Information",
    "Features",
    "Contact Support"
  ];

  const handleQuickReply = (text) => {
    handleSendMessage(text);
  };

  const handleSendMessage = async (text = null) => {
    const messageText = text || inputMessage.trim();
    if (!messageText) return;

    // Add user message
    const userMessage = {
      id: messages.length + 1,
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Call chatbot API
      const response = await api.post('/chatbot', { message: messageText });
      
      if (response.data.success) {
        const botMessage = {
          id: messages.length + 2,
          text: response.data.response,
          sender: 'bot',
          timestamp: new Date(),
          hasAction: response.data.hasAction || false,
          actionType: response.data.actionType || null,
          actionUrl: response.data.actionUrl || null,
          suggestions: response.data.suggestions || null
        };
        setMessages(prev => [...prev, botMessage]);

        // Store data if provided (for pricing, blogs, etc.)
        if (response.data.data) {
          if (response.data.actionType === 'pricing') {
            setPricingData(response.data.data);
          }
        }
      } else {
        // Fallback to local response
        const botResponse = getBotResponse(messageText);
        const botMessage = {
          id: messages.length + 2,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error calling chatbot API:', error);
      // Fallback to local response on error
      const botResponse = getBotResponse(messageText);
      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } finally {
      setIsTyping(false);
      setLoadingPricing(false);
    }
  };

  const getBotResponse = (userMessage, pricingDataFromAPI = null) => {
    const message = userMessage.toLowerCase();
    
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return "Hello! I'm here to help you with Acuit CRM. What would you like to know?";
    }
    
    if (message.includes('price') || message.includes('cost') || message.includes('pricing')) {
      const pricing = pricingDataFromAPI || pricingData;
      
      if (pricing && pricing.length > 0) {
        let response = "Here are our current pricing plans:\n\n";
        pricing.forEach((plan, index) => {
          response += `📦 ${plan.title}\n`;
          response += `   Price: ${plan.price}\n`;
          if (plan.user_range) {
            response += `   ${plan.user_range}\n`;
          }
          if (plan.features && plan.features.length > 0) {
            response += `   Features: ${plan.features.slice(0, 3).join(', ')}${plan.features.length > 3 ? '...' : ''}\n`;
          }
          response += "\n";
        });
        response += "Would you like to see more details or visit our pricing page?";
        return response;
      } else {
        return "Acuit CRM offers flexible pricing plans. Would you like to see our pricing page for detailed information?";
      }
    }
    
    if (message.includes('feature') || message.includes('what can') || message.includes('capabilities')) {
      return "Acuit CRM includes 17 powerful features:\n\n• Project Management\n• Invoicing & Estimates\n• Lead Management\n• Customer Support\n• Contracts & E-Signatures\n• And much more!\n\nVisit our Features page to learn more!";
    }
    
    if (message.includes('contact') || message.includes('support') || message.includes('help')) {
      return "You can reach us at:\n\n📧 Email: mail@plenitudeit.com.my\n📞 Phone: +603-2727 1889\n📍 Address: Level 23, NU Tower 2, Kuala Lumpur\n\nOr fill out our contact form on the website!";
    }
    
    if (message.includes('demo') || message.includes('trial') || message.includes('free')) {
      return "Yes! We offer:\n\n✅ 30-day free trial\n✅ Free demo and consultation\n✅ Easy setup\n\nWould you like to schedule a demo?";
    }
    
    if (message.includes('about') || message.includes('what is acuit')) {
      return "Acuit CRM is a robust, self-hosted Customer Relationship Management system designed for small businesses, freelancers, startups, and growing agencies. It combines CRM, project management, invoicing, and support tools in one integrated platform.";
    }
    
    if (message.includes('install') || message.includes('setup') || message.includes('how to')) {
      return "Acuit CRM is self-hosted, meaning you install it on your own server. We provide:\n\n• Easy installation guide\n• Comprehensive documentation\n• Support during setup\n• Migration assistance\n\nContact us for installation help!";
    }
    
    return "Thank you for your message! Our team will get back to you soon. In the meantime, you can:\n\n• Visit our Features page\n• Check our Pricing\n• Contact us directly\n\nIs there anything else I can help you with?";
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      <div
        className={`chatbot-toggle ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #4A90E2, #48BB78)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(74, 144, 226, 0.4)',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          transition: 'all 0.3s ease',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)'
        }}
      >
        {isOpen ? '✕' : '💬'}
      </div>

      {/* Chatbot Window */}
      {isOpen && (
        <div
          className="chatbot-window"
          style={{
            position: 'fixed',
            bottom: '90px',
            left: '20px',
            width: '350px',
            maxWidth: 'calc(100vw - 40px)',
            height: '500px',
            maxHeight: 'calc(100vh - 120px)',
            background: 'white',
            borderRadius: '15px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'slideUp 0.3s ease'
          }}
        >
          {/* Chatbot Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #4A90E2, #48BB78)',
              color: 'white',
              padding: '15px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}
              >
                🤖
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Acuit CRM Support</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#28a745',
                      boxShadow: '0 0 6px rgba(40, 167, 69, 0.8)',
                      animation: 'pulse 2s infinite'
                    }}
                  ></div>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.9 }}>We're online</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              background: '#f8f9fa'
            }}
          >
            {messages.map((message) => (
              <div key={message.id}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: '15px'
                  }}
                >
                  <div
                    style={{
                      maxWidth: '75%',
                      padding: '10px 15px',
                      borderRadius: '15px',
                      background: message.sender === 'user' 
                        ? 'linear-gradient(135deg, #4A90E2, #48BB78)' 
                        : 'white',
                      color: message.sender === 'user' ? 'white' : '#333',
                      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                      whiteSpace: 'pre-line',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}
                  >
                    {message.text}
                  </div>
                </div>
                {message.hasAction && message.actionUrl && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      marginBottom: '15px',
                      marginLeft: '10px'
                    }}
                  >
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate(message.actionUrl);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #4A90E2, #48BB78)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 500,
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.2)';
                      }}
                    >
                      {message.actionType === 'pricing' && 'View Pricing Page →'}
                      {message.actionType === 'blog' && 'View Blog Articles →'}
                      {message.actionType === 'testimonials' && 'View Testimonials →'}
                      {message.actionType === 'features' && 'View Features →'}
                      {message.actionType === 'contact' && 'Contact Us →'}
                      {!['pricing', 'blog', 'testimonials', 'features', 'contact'].includes(message.actionType) && 'Learn More →'}
                    </button>
                  </div>
                )}
                {message.suggestions && message.suggestions.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginTop: '10px',
                      marginLeft: '10px'
                    }}
                  >
                    {message.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleQuickReply(suggestion)}
                        style={{
                          padding: '6px 12px',
                          background: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: '20px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = '#e9ecef';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = '#f8f9fa';
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginBottom: '15px',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <div
                  style={{
                    padding: '10px 15px',
                    borderRadius: '15px',
                    background: 'white',
                    color: '#333',
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#4A90E2',
                      animation: 'bounce 1.4s infinite ease-in-out both',
                      animationDelay: '0s'
                    }}></div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#4A90E2',
                      animation: 'bounce 1.4s infinite ease-in-out both',
                      animationDelay: '0.2s'
                    }}></div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#4A90E2',
                      animation: 'bounce 1.4s infinite ease-in-out both',
                      animationDelay: '0.4s'
                    }}></div>
                  </div>
                  <span>Typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length === 1 && (
            <div
              style={{
                padding: '10px 20px',
                background: 'white',
                borderTop: '1px solid #e9ecef'
              }}
            >
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>Quick replies:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    style={{
                      padding: '6px 12px',
                      background: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '20px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#e9ecef';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#f8f9fa';
                    }}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div
            style={{
              padding: '15px 20px',
              background: 'white',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              gap: '10px'
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '10px 15px',
                border: '1px solid #dee2e6',
                borderRadius: '25px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputMessage.trim()}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: inputMessage.trim() 
                  ? 'linear-gradient(135deg, #4A90E2, #48BB78)' 
                  : '#ccc',
                border: 'none',
                color: 'white',
                cursor: inputMessage.trim() ? 'pointer' : 'not-allowed',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 6px rgba(40, 167, 69, 0.8);
          }
          50% {
            opacity: 0.7;
            box-shadow: 0 0 12px rgba(40, 167, 69, 1);
          }
        }
        
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .chatbot-toggle:hover {
          transform: scale(1.1) ${isOpen ? 'rotate(45deg)' : 'rotate(0deg)'};
        }
        
        .chatbot-window::-webkit-scrollbar {
          width: 6px;
        }
        
        .chatbot-window::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        .chatbot-window::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 3px;
        }
        
        .chatbot-window::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </>
  );
}

export default Chatbot;

